import React, { useMemo, useState } from 'react';
import { AdminEmptyState, FilterSelect, Panel } from './AdminPrimitives';
import { sportMetricAnalytics } from '../../data/admin';
import { chartableMetrics, sports } from '../../data/sportMetrics';
import { profileFieldsForSport } from '../../data/sportProfileFields';
import { useLocale } from '../../contexts/LocaleContext';
import type { SportId } from '../../types';
import { formatMetric } from '../../utils/units';

/**
 * Aggregated analytics by sport and numeric metric, read from the central catalogue.
 * A sport or metric added to the catalogue appears here automatically; metrics without
 * aggregated data show an empty state. Categorical fields are listed, never analysed numerically.
 */
export function SportMetricAnalytics({
  initialSport
}: {
  initialSport?: SportId;
}) {
  const {
    t,
    tl
  } = useLocale();
  const [sport, setSport] = useState<SportId>(initialSport ?? 'running');
  const metrics = useMemo(() => chartableMetrics(sport), [sport]);
  const withData = metrics.filter(metric => sportMetricAnalytics[metric.id]);
  const categorical = profileFieldsForSport(sport);
  return <Panel title="Métricas por esporte" hint="Uso e mediana agregada de cada métrica numérica do catálogo central." action={<FilterSelect label="Esporte" value={sport} options={sports.map(id => ({
    id,
    label: t(`category.${id}`)
  }))} onChange={value => setSport(value as SportId)} data-id="element-557" data-mp="20rg6dw5mzqfl" />} data-id="element-556" data-mp="1vr1zwijooojo">
      {withData.length === 0 ? <AdminEmptyState title={`Sem dados agregados para ${t(`category.${sport}`)}`} body={`O catálogo já define ${metrics.length} métricas numéricas para este esporte. As análises aparecem quando houver registros agregados.`} data-id="element-558" data-mp="2ena55mcp4zez" /> : <ul data-id="element-559" data-mp="2tek4ria856ie">
          {metrics.map(metric => {
        const data = sportMetricAnalytics[metric.id];
        return <li key={metric.id} className="flex items-center gap-3 border-b border-sand-line py-2 last:border-b-0" data-id="element-560" data-mp="2xfvurox0ftr4">
                <span className="min-w-0 flex-1 text-[12.5px] font-medium text-deep" data-id="element-561" data-mp="tcav5vr7b5zz">
                  {tl(metric.label)}
                </span>
                {data ? <>
                    <span className="hidden h-[6px] w-28 overflow-hidden rounded-pill bg-sand-deep sm:block" aria-hidden="true" data-id="element-562" data-mp="12bxvsfc3w7rz">
                      <span className="block h-full rounded-pill bg-gold" style={{
                width: `${data.usage}%`
              }} data-id="element-563" data-mp="1gzixc4tc1yxs" />
                    </span>
                    <span className="bee-num w-24 text-right text-[12px] text-muted" data-id="element-564" data-mp="1q1s7gtkovqan">{data.usage}% registram</span>
                    <span className="bee-num w-24 text-right text-[12.5px] font-bold text-deep" data-id="element-565" data-mp="1m0gi8p5q972z">
                      {formatMetric(metric.kind, data.median, 'metric')}
                    </span>
                  </> : <span className="text-[11.5px] text-muted" data-id="element-566" data-mp="3nsv3ny7hcto5">sem dados</span>}
              </li>;
      })}
        </ul>}

      {categorical.length > 0 && <p className="mt-3 text-[11.5px] leading-snug text-muted" data-id="element-567" data-mp="3hnx0xefkyjcm">
          Campos de perfil ({categorical.map(field => tl(field.label)).join(', ')}) são categóricos e
          não entram em análises numéricas.
        </p>}
      <p className="mt-2 text-[11.5px] leading-snug text-muted" data-id="element-568" data-mp="38usbjdrr1ap2">
        Sempre em agregado. Valores informados pelos usuários, sem interpretação clínica.
      </p>
    </Panel>;
}